import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "../../../utils/useTranslation";
import { StyleSheet, TouchableOpacity, TextInput, ScrollView } from "react-native";
import { Text, View } from "../../shared/Themed";
import FeatherIcon from "react-native-vector-icons/Feather";
import { ReadOnlyProps } from "../../../utils/templates";
import DeviceOrientationContext from "../../context/DeviceOrientationContext";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";

interface Props {
  childern?: React.ElementType;
  titleName: string | React.ReactNode;
  titlePrefix?: React.ReactNode;
  titleSuffix?: React.ReactNode;
  isSmallDevice?: boolean;
  isMobileDevice?: boolean;
  isEditable?: boolean;
  ref?: any;
  isResetFields?: boolean;
  handleSearch: (searchText: string) => void;
  setRefresh: (event: any) => void;
  setEditable?: (event: any) => void;
  middleSearchControls?: React.ReactNode;

  //for excel download
  dataToExport?: Array<unknown>;
  fileName?: string;
  sheetName?: string;

  // J
  padding?: boolean;
  disableTitle?: boolean;
  noBorder?: boolean;
  extraControls?: React.ReactNode;
  onExport?: () => void;
  onSearchTextChange?: (text: string) => void;
  _searchText?: string;
  expandableSearchOnMobile?: boolean;
  preSearchControls?: React.ReactNode;
}

export const LabelBannerWithSearchBar = (props: ReadOnlyProps<Props>) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const Children = (props.childern || {}) as React.ElementType;
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext
  );
  const { t } = useTranslation();

  const [_searchText, setSearchText] = useState<string>("");
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);

  useEffect(() => {
    const activeSearch = [...searchTags];
    if (_searchText.trim().length > 0) {
      activeSearch.push(_searchText.trim());
    }
    props.handleSearch(activeSearch.join(" "));
  }, [_searchText, searchTags]);

  useEffect(() => {
    if (props.isResetFields) {
      setSearchText("");
      setSearchTags([]);
    }
  }, [props.isResetFields]);

  const submitSearch = () => {
    const text = _searchText.trim();
    if (text.length > 0) {
      if (!searchTags.includes(text)) {
        setSearchTags([...searchTags, text]);
      }
      setSearchText("");
    }
  };

  const isExportAllowed = () => {
    return (
      !isSmallDevice &&
      !isMobileDevice &&
      props.dataToExport &&
      props.dataToExport.length > 0
    );
  };

  const isMobile = isSmallDevice || isMobileDevice;
  const showInlineTitle = !isMobile && props.disableTitle == undefined;

  return (
    <View>
      <View style={styles.container}>
        {/* Search row */}
        <View
          style={[
            styles.searchRow,
            showInlineTitle && { justifyContent: "space-between" },
            props.isEditable && { backgroundColor: "#FEF3C7" },
            isMobile && styles.searchRowMobile,
          ]}
        >
          {/* Inline title for desktop */}
          {showInlineTitle && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              {props.titlePrefix}
              <Text style={styles.inlineTitle}>{props.titleName}</Text>
              {props.titleSuffix}
            </View>
          )}

          {/* Search controls - grouped on right for desktop */}
          <View style={[
            styles.searchControls,
            !showInlineTitle && { width: "100%" },
            isMobile && styles.searchControlsMobile,
          ]}>
            {props.isEditable && (
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => props.setEditable?.(!props.isEditable)}
              >
                <Text style={styles.cancelButtonText}>{t("cancel")}</Text>
              </TouchableOpacity>
            )}

            {props.preSearchControls}

            {props.expandableSearchOnMobile && isMobile && !isSearchExpanded ? (
              <TouchableOpacity
                style={[styles.refreshButton, isMobile && styles.buttonMobile]}
                onPress={() => setIsSearchExpanded(true)}
              >
                <FeatherIcon name="search" size={isMobile ? 14 : 15} color="#6B7280" />
              </TouchableOpacity>
            ) : (
              <>
                {!props.expandableSearchOnMobile && (
                  <TouchableOpacity
                    style={[styles.refreshButton, isMobile && styles.buttonMobile]}
                    onPress={() => {
                      setSearchText("");
                      setSearchTags([]);
                      if (props.setRefresh) {
                        props.setRefresh(true);
                      }
                    }}
                  >
                    <FeatherIcon name="refresh-cw" size={isMobile ? 14 : 15} color="#6B7280" />
                  </TouchableOpacity>
                )}
                
                {props.middleSearchControls}

                <View style={[
                  styles.searchContainer,
                  showInlineTitle && styles.searchContainerCompact,
                  isMobile && styles.searchContainerMobile,
                ]}>
                <FeatherIcon
                  name="search"
                  size={isMobile ? 14 : 16}
                  color="#9CA3AF"
                  style={{ marginRight: 8 }}
                />
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}
                  style={{ flex: 1, marginRight: 8 }}
                >
                  {searchTags.map((tag, index) => (
                    <View
                      key={index}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        backgroundColor: "#EBF5FF",
                        borderRadius: 4,
                        paddingHorizontal: 6,
                        paddingVertical: 2,
                        marginRight: 6,
                      }}
                    >
                      <Text style={{ fontSize: 12, color: "#1D4ED8", marginRight: 4 }}>
                        {tag}
                      </Text>
                      <TouchableOpacity
                        onPress={() => setSearchTags(searchTags.filter((_, i) => i !== index))}
                      >
                        <FeatherIcon name="x" size={12} color="#1D4ED8" />
                      </TouchableOpacity>
                    </View>
                  ))}
                  <TextInput
                    ref={props.ref}
                    autoFocus={true}
                    style={[{ minWidth: 100 }, styles.searchInput, isMobile && styles.searchInputMobile]}
                    onChangeText={(value) => setSearchText(value)}
                    value={_searchText || ""}
                    placeholder={searchTags.length > 0 ? "" : t("searchPlaceholder")}
                    placeholderTextColor="#9CA3AF"
                    onKeyPress={({ nativeEvent }) => {
                      if (nativeEvent.key === "Backspace" && !_searchText && searchTags.length > 0) {
                        setSearchTags(searchTags.slice(0, -1));
                      }
                    }}
                    blurOnSubmit={false}
                    onSubmitEditing={() => submitSearch()}
                  />
                </ScrollView>
                {props.expandableSearchOnMobile ? (
                  (isMobile || searchTags.length > 0 || _searchText.length > 0) && (
                    <TouchableOpacity
                      onPress={() => {
                        if (isMobile) setIsSearchExpanded(false);
                        setSearchText("");
                        setSearchTags([]);
                        if (props.setRefresh) {
                          props.setRefresh(true);
                        }
                      }}
                      style={{ padding: 4 }}
                    >
                      <FeatherIcon name="x" size={isMobile ? 14 : 16} color="#9CA3AF" />
                    </TouchableOpacity>
                  )
                ) : null}
              </View>
              </>
            )}

            {/* Right side controls */}
            <View style={{ marginLeft: "auto", flexDirection: "row", alignItems: "center", gap: styles.searchControls.gap }}>
              {/* Extra controls (Search By) always rendered to the right */}
              {props.extraControls}

            {(isExportAllowed() || props.onExport) && (
              <TouchableOpacity
                style={[styles.exportButton, isMobile && styles.buttonMobile]}
                onPress={() => {
                  if (props.onExport) {
                    props.onExport();
                  } else {
                    exportDataToExcel(
                      props.dataToExport || [],
                      props.fileName,
                      props.sheetName
                    );
                  }
                }}
              >
                <FeatherIcon name="download" size={isMobile ? 14 : 15} color={colors.textSecondary} />
              </TouchableOpacity>
            )}
            </View>

          </View>
        </View>

        {props.childern && <Children {...props} />}
      </View>
    </View >
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      width: "100%",
    },
    titleRow: {
      paddingHorizontal: 4,
      paddingBottom: 8,
    },
    title: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
    },
    inlineTitle: {
      fontSize: 14,
      fontWeight: "700",
      color: colors.text,
      flexShrink: 0,
    },
    searchRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      width: "100%",
      paddingVertical: 12,
      paddingHorizontal: 12,
      minHeight: 56,
    },
    searchRowMobile: {
      paddingVertical: 8,
      paddingHorizontal: 8,
      minHeight: 40,
      gap: 6,
    },
    searchControls: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    searchControlsMobile: {
      gap: 6,
    },
    searchContainerCompact: {
      width: 400,
      flex: undefined,
    },
    searchContainer: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 10,
      paddingHorizontal: 12,
      height: 44,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: colors.text,
      paddingVertical: 0,
      outlineStyle: "none",
    } as any,
    searchInputMobile: {
      fontSize: 13,
    },
    searchContainerMobile: {
      height: 34,
      borderRadius: 8,
      paddingHorizontal: 8,
    },
    buttonMobile: {
      width: 34,
      height: 34,
      borderRadius: 8,
    },
    refreshButton: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    exportButton: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    cancelButton: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
      backgroundColor: colors.textSecondary,
    },
    cancelButtonText: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600",
    },
  });
}

